import type { GameId } from '../api/types';
import { DEFAULT_GAME_ID, normalizeGameId } from '../data/games';
import type { DensityName, ThemeName } from '../theme/tokens';

/** Controls condensed vs full Stats + Rule Builder UI (DesignFiles simple/advanced). */
export type ExperienceMode = 'simple' | 'advanced';

export interface NotifPrefs {
  draws: boolean;
  jackpot: boolean;
  results: boolean;
  weekly: boolean;
}

export interface UserPrefs {
  themeName: ThemeName;
  dark: boolean;
  densityName: DensityName;
  notif: NotifPrefs;
  /** ISO `YYYY-MM-DD`. Empty string means not set. */
  birthday: string;
  /** True once we've probed Google (or given up) so the silent import never runs again. */
  birthdayImported: boolean;
  /** Active game used by Home, Stats, Rules, and Scan. */
  gameId: GameId;
  /** Simple = glanceable stats + Smart pick; Advanced = full analytics + rule stack. */
  experienceMode: ExperienceMode;
}

export const DEFAULT_PREFS: UserPrefs = {
  themeName: 'sunrise',
  dark: false,
  densityName: 'comfy',
  notif: { draws: true, jackpot: true, results: false, weekly: true },
  birthday: '',
  birthdayImported: false,
  gameId: DEFAULT_GAME_ID,
  experienceMode: 'simple',
};

const THEME_NAMES: ThemeName[] = ['sunrise', 'neon', 'clover'];
const DENSITY_NAMES: DensityName[] = ['compact', 'comfy', 'spacious'];
const EXPERIENCE_MODES: ExperienceMode[] = ['simple', 'advanced'];

function isValidBirthday(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (value === '') return true;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return false;
  if (parsed > Date.now()) return false;
  return true;
}

export function sanitizeUserPrefs(raw: unknown): UserPrefs {
  const p = (raw ?? {}) as Partial<UserPrefs> & { notif?: Partial<NotifPrefs> };
  const themeName = THEME_NAMES.includes(p.themeName as ThemeName)
    ? (p.themeName as ThemeName)
    : DEFAULT_PREFS.themeName;
  const densityName = DENSITY_NAMES.includes(p.densityName as DensityName)
    ? (p.densityName as DensityName)
    : DEFAULT_PREFS.densityName;
  return {
    themeName,
    dark: typeof p.dark === 'boolean' ? p.dark : DEFAULT_PREFS.dark,
    densityName,
    notif: {
      draws: typeof p.notif?.draws === 'boolean' ? p.notif.draws : DEFAULT_PREFS.notif.draws,
      jackpot: typeof p.notif?.jackpot === 'boolean' ? p.notif.jackpot : DEFAULT_PREFS.notif.jackpot,
      results: typeof p.notif?.results === 'boolean' ? p.notif.results : DEFAULT_PREFS.notif.results,
      weekly: typeof p.notif?.weekly === 'boolean' ? p.notif.weekly : DEFAULT_PREFS.notif.weekly,
    },
    birthday: isValidBirthday(p.birthday) ? p.birthday : DEFAULT_PREFS.birthday,
    birthdayImported:
      typeof p.birthdayImported === 'boolean' ? p.birthdayImported : DEFAULT_PREFS.birthdayImported,
    gameId: normalizeGameId(p.gameId),
    experienceMode: EXPERIENCE_MODES.includes(p.experienceMode as ExperienceMode)
      ? (p.experienceMode as ExperienceMode)
      : DEFAULT_PREFS.experienceMode,
  };
}
